import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { sfx } from "../lib/sound";
import { fetchOrCreateProfile, getStoredName, getOrCreateUserId, API } from "../lib/api";
import { getPublicUrl } from "../lib/publicUrl";
import { PLAYERS } from "../lib/quattro";
import { QuattroSocket } from "../lib/wsClient";
import { getInitials } from "../lib/avatar";
import ChatPanel from "../components/ChatPanel";

const SEAT_NAMES = ["Yellow Clubs", "Cyan Diamonds", "Crimson Hearts", "Violet Spades"];

export default function OnlineRoom({ mode = "join" }) {
  const nav = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const codeFromRoute = params.code || searchParams.get("code") || "";

  const [profile, setProfile] = useState(null);
  const [code, setCode] = useState(codeFromRoute);
  const [room, setRoom] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sock, setSock] = useState(null);
  const [mySeat, setMySeat] = useState(null);
  const [copied, setCopied] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [iouOpen, setIouOpen] = useState(false);
  const [iouPresets, setIouPresets] = useState([]);
  const [iouSelected, setIouSelected] = useState(null);
  const [iouCustom, setIouCustom] = useState("");
  const [iouSaved, setIouSaved] = useState(null);
  const [iouBusy, setIouBusy] = useState(false);
  const autoJoinRef = useRef(false);
  const autoCreateRef = useRef(false);

  const userId = getOrCreateUserId();

  useEffect(() => {
    (async () => {
      try {
        const p = await fetchOrCreateProfile(getStoredName());
        setProfile(p);
      } catch (e) { console.error(e); }
    })();
    axios.get(`${API}/iou/presets`).then((r) => {
      setIouPresets(r.data?.presets || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!profile) return;
    if (mode === "join" && codeFromRoute && !room && !autoJoinRef.current) {
      autoJoinRef.current = true;
      setCode(codeFromRoute);
      doJoinWith(codeFromRoute);
    }
    // Casual rooms: auto-create on mount, no bet-picker.
    if (mode === "create" && !room && !autoCreateRef.current) {
      autoCreateRef.current = true;
      doCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, mode]);

  // Safety net: if room creation/join didn't complete within 8 seconds, surface
  // a "back to lobby" path instead of leaving the user stuck on "Preparing
  // room…". Common cause: profile fetch failed and `doCreate` was never armed.
  useEffect(() => {
    if (room || error) return;
    if (!autoCreateRef.current && !autoJoinRef.current) return;
    const t = setTimeout(() => {
      if (!room) {
        setError("Couldn't reach the matchmaking server. Tap Back, pull-to-refresh the Lobby, and try again.");
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [room, error]);

  useEffect(() => {
    if (!room || !profile) return;
    const code_ = room.code;
    const s = new QuattroSocket({
      code: code_, user_id: userId, name: profile.name, avatar: profile.avatar || "default",
    });
    const unsub = s.subscribe((msg) => {
      if (msg.type === "joined") {
        setMySeat(msg.seat);
        setRoom(msg.room);
      } else if (msg.type === "roomState") {
        setRoom(msg.room);
        // Status-change navigation moved to a dedicated useEffect below so it
        // also handles the polling-fallback path for late-WS-connection cases
        // (e.g. friend joined but their WS dropped before host pressed Start).
      } else if (msg.type === "chat") {
        setChatMessages((prev) => [...prev, {
          seat: msg.seat, name: msg.name, text: msg.text, ts: msg.ts,
        }].slice(-100));
      } else if (msg.type === "error") {
        setError(msg.msg || "Error");
      }
    });
    s.connect();
    setSock(s);
    return () => { unsub(); s.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.code, profile?.user_id]);

  // Robust nav-on-start: any time we see room.status flip to "playing",
  // navigate. This catches both WS-driven and poll-driven updates so friends
  // can't get stuck on the waiting screen if their WS lagged.
  useEffect(() => {
    if (room?.status === "playing" && room?.code) {
      nav(`/online/play/${room.code}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.status, room?.code]);

  // Polling fallback: every 3s while in the waiting room, fetch the room
  // via REST. Cheap insurance against missed WS events. Stops once status
  // changes (the nav effect handles teardown via unmount).
  useEffect(() => {
    if (!room?.code || room.status !== "waiting") return undefined;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/rooms/${room.code}`);
        if (r?.data?.status && r.data.status !== room.status) {
          setRoom(r.data);
        }
      } catch {
        // Silently ignore — WS is the source of truth, this is just a safety net
      }
    }, 3000);
    return () => clearInterval(id);
  }, [room?.code, room?.status]);

  const doCreate = async () => {
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${API}/rooms`, {
        user_id: userId, name: profile.name, avatar: profile.avatar || "default",
      });
      setRoom(res.data.room);
      sfx.click();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create room");
    }
    setBusy(false);
  };

  const doJoinWith = async (joinCode) => {
    if (!joinCode || joinCode.length < 4) { setError("Enter a valid room code."); return; }
    setBusy(true); setError("");
    try {
      const res = await axios.post(`${API}/rooms/${joinCode}/join`, {
        user_id: userId, name: profile.name, avatar: profile.avatar || "default",
      });
      setRoom(res.data.room);
      sfx.click();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to join room");
    }
    setBusy(false);
  };

  const doJoin = () => doJoinWith(code);

  const inviteUrl = room ? `${getPublicUrl()}/online/room/${room.code}` : "";
  const qrSrc = inviteUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&bgcolor=0B0C10&color=FFFFFF&data=${encodeURIComponent(inviteUrl)}`
    : "";

  const copyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      sfx.click();
      setTimeout(() => setCopied(false), 1800);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = inviteUrl; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
      document.body.removeChild(ta);
    }
  };

  const nativeShare = async () => {
    if (!inviteUrl) return;
    if (!navigator.share) { copyInvite(); return; }
    try {
      await navigator.share({
        title: "Join my Quattro game",
        text: `Play Quattro with me! Room ${room.code}`,
        url: inviteUrl,
      });
      sfx.click();
    } catch {}
  };

  const start = () => {
    sock?.send({ type: "start" });
    sfx.click();
  };

  const submitIOU = async () => {
    const stake = iouSelected === "custom"
      ? iouCustom.trim()
      : (iouPresets.find((p) => p.id === iouSelected)?.label || "");
    if (!stake) { setError("Pick or type a stake."); return; }
    if (!room) return;
    const humans = (room.players || []).filter((p) => p && !p.isBot);
    setIouBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/iou/create`, {
        user_id: userId,
        room_code: room.code,
        stake_label: stake,
        // Send whoever is currently in the room (host alone is OK — friends
        // who join later are auto-merged at settle time on the backend).
        participants: humans.map((p) => p.userId),
        participant_names: Object.fromEntries(humans.map((p) => [p.userId, p.name])),
      });
      setIouSaved({ stake_label: stake, iou_id: r.data.iou_id });
      sfx.click();
      setTimeout(() => { setIouOpen(false); setIouSelected(null); setIouCustom(""); }, 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create IOU");
    } finally {
      setIouBusy(false);
    }
  };

  const isHost = room && profile && room.hostUserId === userId;
  const isCasual = !!room?.casual;

  // Paid-tournament human queue: keep the Start button disabled until either
  // 4 humans are present OR 60s have elapsed since the room was created.
  // Freeroll is exempt (entry_fee=0). Mirrors backend rooms.start_game gate.
  const PAID_QUEUE_SEC = 60;
  const FREE_TOURNAMENTS = new Set(["freeroll"]);
  const isPaidTournament = !!room?.tournamentId && !FREE_TOURNAMENTS.has(room.tournamentId);
  const humans = (room?.players || []).filter((p) => p && !p.isBot).length;
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    if (!isPaidTournament) return undefined;
    const id = setInterval(() => setTick(Date.now()), 500);
    return () => clearInterval(id);
  }, [isPaidTournament]);
  const elapsedSec = room?.createdAt ? Math.max(0, (tick / 1000) - room.createdAt) : 0;
  const queueWaiting = isPaidTournament && humans < 4 && elapsedSec < PAID_QUEUE_SEC;
  const queueSecsLeft = queueWaiting ? Math.max(0, Math.ceil(PAID_QUEUE_SEC - elapsedSec)) : 0;

  return (
    <div className="min-h-screen w-full px-4 py-6 md:py-16 flex flex-col items-center">
      <div className="max-w-2xl w-full">
        <div className="flex items-center justify-between gap-2 mb-6 md:mb-8 flex-wrap">
          <h1 className="font-display font-black text-2xl md:text-4xl">
            {mode === "create" ? "Friend Match" : "Join Game"}
          </h1>
          <div className="flex items-center gap-2 md:gap-3">
            {profile && (
              <div className="glass rounded-full px-2.5 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5 md:gap-2" data-testid="online-room-chips" style={{ minHeight: 32 }}>
                <span style={{ color: "#FFD700", fontSize: 14 }}>◆</span>
                <span className="font-mono font-bold" style={{ fontSize: 12 }}>{profile.chips.toLocaleString()}</span>
              </div>
            )}
            <button className="btn-ghost" onClick={() => nav("/")} data-testid="online-back-btn">Back</button>
          </div>
        </div>

        {error && (
          <div className="glass rounded-2xl p-4 mb-4 text-sm" style={{ border: "1px solid rgba(255,42,42,0.4)", color: "#FFB4B4" }} data-testid="online-error">
            {error}
          </div>
        )}

        {!room ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-6 md:p-8">
            {mode === "create" ? (
              <div className="text-center text-white/60 text-sm">
                {busy ? "Setting up your friend room…" : "Preparing room…"}
              </div>
            ) : (
              <div>
                <div className="label-eyebrow mb-2">Room Code</div>
                <input
                  className="w-full bg-white/10 border border-white/15 rounded-xl px-4 py-4 text-2xl font-mono font-bold tracking-[0.4em] text-center outline-none focus:border-white/40 transition-colors"
                  placeholder="000000"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                  data-testid="room-code-input"
                />
                <div className="flex justify-center mt-4">
                  <button className="btn-primary" onClick={doJoin} disabled={busy} data-testid="join-room-btn">
                    {busy ? "Joining..." : "Join"}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-6 md:p-8">
            {isCasual && (
              <div
                className="rounded-2xl px-4 py-3 mb-5 flex items-center gap-3"
                style={{
                  background: "linear-gradient(135deg, rgba(61,220,151,0.10), rgba(0,229,255,0.08))",
                  border: "1px solid rgba(61,220,151,0.45)",
                }}
                data-testid="casual-banner"
              >
                <span style={{ fontSize: 22 }}>🤝</span>
                <div className="flex-1">
                  <div className="font-display font-black text-sm" style={{ color: "#3DDC97" }}>
                    CASUAL FRIEND MATCH
                  </div>
                  <div className="text-xs text-white/65">
                    No chips on the line — just bragging rights. Wager an IOU
                    like <i>"loser buys ice cream"</i> for extra spice.
                  </div>
                </div>
              </div>
            )}

            {room.tournamentId && (
              <div
                className="rounded-2xl px-4 py-3 mb-5 flex items-center gap-3"
                style={{
                  background: "rgba(255,215,0,0.08)",
                  border: "1px solid rgba(255,215,0,0.4)",
                }}
                data-testid="tournament-banner"
              >
                <span style={{ fontSize: 22 }}>🏆</span>
                <div className="flex-1">
                  <div className="font-display font-black text-sm" style={{ color: "#FFD700" }}>
                    TOURNAMENT HEAT
                    {room.seriesTarget > 1 && (
                      <span className="ml-2 label-eyebrow" style={{ color: "#B537F2", fontSize: 10 }}>
                        BEST OF {room.seriesTarget * 2 - 1}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/65">
                    Winner takes the pot
                    {room.bonusGems > 0 && (
                      <> + <b style={{ color: "#00E5FF" }}>◈ {room.bonusGems} bonus gems</b></>
                    )}
                    {room.bonusChips > 0 && (
                      <> + <b style={{ color: "#FFD700" }}>◆ {room.bonusChips.toLocaleString()} chips</b></>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="text-center mb-6">
              <div className="label-eyebrow">Room Code</div>
              <div className="font-display font-black text-5xl md:text-6xl tracking-[0.3em] mt-2"
                style={{ background: "linear-gradient(90deg,#FFD700,#00E5FF,#FF2A2A,#B537F2)", WebkitBackgroundClip: "text", color: "transparent" }}
                data-testid="room-code-display">
                {room.code}
              </div>
              <div className="text-white/55 text-xs mt-2">Share this code or invite link with friends.</div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6 items-center" data-testid="share-block">
              <div className="flex flex-col items-center md:items-start gap-3">
                <div className="label-eyebrow">Invite Link</div>
                <div className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 font-mono text-xs text-white/80 break-all" data-testid="invite-link">
                  {inviteUrl}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest"
                    onClick={copyInvite}
                    data-testid="copy-invite-btn"
                    style={{
                      background: copied ? "#FFD700" : "#fff",
                      color: "#000",
                      transition: "background .2s ease",
                    }}
                  >
                    {copied ? "Copied!" : "Copy Link"}
                  </button>
                  <button
                    className="btn-ghost"
                    style={{ padding: "8px 14px", fontSize: 11 }}
                    onClick={nativeShare}
                    data-testid="share-invite-btn"
                  >
                    Share…
                  </button>
                </div>
                <div className="text-white/45 text-xs">Friends tap the link → land directly in this room.</div>
              </div>
              <div className="flex justify-center md:justify-end">
                <div className="rounded-2xl p-3 bg-white" data-testid="invite-qr">
                  {qrSrc && (
                    <img src={qrSrc} alt="Scan to join" width={164} height={164}
                      style={{ display: "block", borderRadius: 8 }} />
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="room-players">
              {[0, 1, 2, 3].map((seat) => {
                const p = room.players[seat];
                const pp = PLAYERS[seat];
                return (
                  <div key={seat} className="rounded-2xl p-3 text-center"
                    style={{
                      background: p ? `linear-gradient(155deg, ${pp.color}22, transparent)` : "rgba(255,255,255,0.03)",
                      border: `1px solid ${p ? pp.color : "rgba(255,255,255,0.08)"}`,
                    }}
                    data-testid={`seat-${seat}`}
                  >
                    <div className="font-display font-black mx-auto rounded-full flex items-center justify-center"
                      style={{
                        width: 38, height: 38, background: pp.color,
                        color: seat === 0 ? "#0B0C10" : "#fff",
                        fontSize: p && !p.isBot ? 14 : 16,
                        letterSpacing: p && !p.isBot ? 0.5 : 0,
                        boxShadow: `0 0 12px ${pp.glow}`,
                      }}
                      data-testid={`seat-avatar-${seat}`}
                    >{p && !p.isBot ? getInitials(p.name) : pp.suit}</div>
                    <div className="font-display font-bold mt-2 text-sm truncate">
                      {p ? p.name : "Open"}
                    </div>
                    <div className="label-eyebrow mt-1" style={{ fontSize: 9 }}>
                      {p ? (p.isBot ? "BOT" : (p.connected ? `LV ${p.level || 1}` : "OFFLINE")) : SEAT_NAMES[seat]}
                    </div>
                    {p && !p.isBot && !isCasual && (
                      <div className="mt-1 font-mono" style={{ fontSize: 11, color: "#FFD700" }} data-testid={`seat-chips-${seat}`}>
                        ◆ {(p.chips || 0).toLocaleString()}
                      </div>
                    )}
                    {p && p.isBot && (
                      <div className="mt-1 label-eyebrow" style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>—</div>
                    )}
                  </div>
                );
              })}
            </div>

            {!isCasual && (
              <div className="mt-5 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
                style={{ background: "rgba(255,215,0,0.07)", border: "1px solid rgba(255,215,0,0.25)" }}
                data-testid="ante-banner">
                <div>
                  <div className="label-eyebrow" style={{ color: "rgba(255,215,0,0.9)", fontSize: 9 }}>ANTE PER PLAYER</div>
                  <div className="font-mono font-bold" style={{ fontSize: 22, color: "#FFD700" }} data-testid="ante-amount">◆ {room.bet}</div>
                </div>
                <div className="text-right">
                  <div className="label-eyebrow" style={{ fontSize: 9 }}>WINNER TAKES</div>
                  <div className="font-mono font-bold" style={{ fontSize: 22, color: "#FFD700" }} data-testid="ante-pot">◆ {room.bet * 4}</div>
                </div>
              </div>
            )}

            {iouSaved && (
              <div className="mt-4 rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ background: "rgba(61,220,151,0.10)", border: "1px solid rgba(61,220,151,0.45)" }}
                data-testid="iou-saved">
                <span style={{ fontSize: 18 }}>📝</span>
                <div className="text-sm">
                  <span className="text-white/70">Stake on the line: </span>
                  <b style={{ color: "#3DDC97" }}>{iouSaved.stake_label}</b>
                </div>
              </div>
            )}

            <div className="flex flex-col items-center gap-3 mt-6">
              {isCasual && !iouSaved && (
                <button
                  className="btn-ghost"
                  style={{ borderColor: "rgba(61,220,151,0.5)", color: "#3DDC97" }}
                  onClick={() => { sfx.click(); setIouOpen(true); }}
                  data-testid="open-iou-btn"
                >
                  📝 Wager an IOU (loser pays)
                </button>
              )}
              {isHost ? (
                <button
                  className="btn-primary"
                  onClick={start}
                  disabled={queueWaiting}
                  data-testid="start-game-btn"
                  style={queueWaiting ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  {queueWaiting
                    ? `Waiting for humans · ${queueSecsLeft}s`
                    : `Start Game ${room.players.filter(Boolean).length < 4 ? "(fill with bots)" : ""}`}
                </button>
              ) : (
                <div className="label-eyebrow">Waiting for host…</div>
              )}
              <div className="text-white/50 text-xs">
                {room.players.filter(Boolean).length}/4 players
                {!isCasual && (
                  <> · You ante <b style={{ color: "#FFD700" }}>◆{room.bet}</b> when host starts</>
                )}
                {isCasual && <> · No chips on the line — just glory</>}
                {queueWaiting && (
                  <div className="mt-1" style={{ color: "rgba(181,55,242,0.85)" }} data-testid="paid-queue-note">
                    🏆 Paid tournament — bot-fill unlocks in <b>{queueSecsLeft}s</b> if humans don't arrive
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
      {room && (
        <ChatPanel
          messages={chatMessages}
          mySeat={mySeat}
          players={room.players}
          onSend={(text) => sock?.send({ type: "chat", text })}
        />
      )}

      <AnimatePresence>
        {iouOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center px-4"
            style={{ background: "rgba(0,0,0,0.78)" }}
            data-testid="iou-modal"
          >
            <motion.div
              initial={{ y: 20, scale: 0.96 }} animate={{ y: 0, scale: 1 }}
              className="glass rounded-3xl p-6 max-w-md w-full"
              style={{
                border: "1px solid rgba(61,220,151,0.5)",
                maxHeight: "92vh", overflowY: "auto",
              }}
            >
              <div className="label-eyebrow mb-2" style={{ color: "#3DDC97" }}>IOU WAGER</div>
              <h2 className="font-display font-black text-2xl mb-1">Loser pays…</h2>
              <p className="text-white/65 text-sm mb-3">
                Pure social stake. We'll track who owes whom. No chips change hands.
              </p>
              {/* When host locks in early (alone), let them know friends auto-join the wager. */}
              {(room.players || []).filter((p) => p && !p.isBot).length < 2 && (
                <div
                  className="rounded-xl px-3 py-2 mb-3 text-xs"
                  style={{
                    background: "rgba(255,215,0,0.08)",
                    border: "1px solid rgba(255,215,0,0.30)",
                    color: "rgba(255,215,0,0.90)",
                  }}
                  data-testid="iou-pre-friend-note"
                >
                  Heads up: you're alone in the room. Friends who join will
                  automatically be on the hook for this wager.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {iouPresets.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setIouSelected(p.id); setIouCustom(""); }}
                    className="rounded-2xl px-3 py-3 text-left transition-all"
                    style={{
                      background: iouSelected === p.id ? "rgba(61,220,151,0.18)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${iouSelected === p.id ? "rgba(61,220,151,0.6)" : "rgba(255,255,255,0.08)"}`,
                    }}
                    data-testid={`iou-preset-${p.id}`}
                  >
                    <span style={{ fontSize: 18, marginRight: 6 }}>{p.emoji}</span>
                    <span className="font-display font-bold text-sm">{p.label.replace(p.emoji + " ", "")}</span>
                  </button>
                ))}
              </div>
              <div className="mb-4">
                <button
                  onClick={() => setIouSelected("custom")}
                  className="text-xs underline text-white/55 hover:text-white"
                >+ Custom stake</button>
                {iouSelected === "custom" && (
                  <input
                    className="w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2 mt-2 outline-none focus:border-white/40 text-sm"
                    placeholder="e.g. 'Loser does the dishes for a week'"
                    value={iouCustom}
                    maxLength={80}
                    onChange={(e) => setIouCustom(e.target.value)}
                    data-testid="iou-custom-input"
                  />
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setIouOpen(false); setIouSelected(null); setIouCustom(""); setError(""); }}
                  className="btn-ghost flex-1"
                  data-testid="iou-cancel-btn"
                >Cancel</button>
                <button
                  onClick={submitIOU}
                  disabled={iouBusy || !iouSelected || (iouSelected === "custom" && !iouCustom.trim())}
                  className="btn-primary flex-1"
                  data-testid="iou-confirm-btn"
                >{iouBusy ? "Saving…" : "Lock it in"}</button>
              </div>
              {error && (
                <div className="mt-3 text-center text-xs" style={{ color: "#FFB4B4" }} data-testid="iou-error">
                  {error}
                </div>
              )}
              {iouSaved && (
                <div className="mt-3 text-center text-xs" style={{ color: "#3DDC97" }} data-testid="iou-success">
                  Wager locked in — good luck!
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
